import type { TableBlockContent } from "../types"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"

interface TableBlockEditorProps {
  content: TableBlockContent | null
  onChange: (content: TableBlockContent) => void
}

export function TableBlockEditor({ content, onChange }: TableBlockEditorProps) {
  const c = content ?? {}
  const headers = c.headers ?? ["", ""]
  const rows = c.rows ?? [["", ""]]
  const update = (partial: Partial<TableBlockContent>) =>
    onChange({ ...c, ...partial })
  const setHeader = (i: number, val: string) => {
    const next = [...headers]
    next[i] = val
    update({ headers: next })
  }
  const setCell = (rowI: number, colI: number, val: string) => {
    const next = rows.map((r, i) =>
      i === rowI
        ? r.map((cell, j) => (j === colI ? val : cell))
        : [...r]
    ) as string[][]
    update({ rows: next })
  }
  const addColumn = () => {
    update({
      headers: [...headers, ""],
      rows: rows.map((r) => [...r, ""]),
    })
  }
  const addRow = () => {
    update({ rows: [...rows, headers.map(() => "")] })
  }
  const removeColumn = (colI: number) => {
    update({
      headers: headers.filter((_, i) => i !== colI),
      rows: rows.map((r) => r.filter((_, i) => i !== colI)),
    })
  }
  const removeRow = (rowI: number) => {
    update({ rows: rows.filter((_, i) => i !== rowI) })
  }

  return (
    <Card>
      <CardHeader>
        <h3 className="text-base font-medium">Table</h3>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Heading</Label>
          <Input
            value={c.heading ?? ""}
            onChange={(e) => update({ heading: e.target.value })}
            placeholder="Table heading"
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Headers</Label>
            <Button type="button" variant="outline" size="sm" onClick={addColumn}>
              Add column
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {headers.map((h, i) => (
              <div key={i} className="flex gap-1 items-center">
                <Input
                  value={h}
                  onChange={(e) => setHeader(i, e.target.value)}
                  placeholder={`Col ${i + 1}`}
                  className="w-24"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeColumn(i)}
                >
                  ×
                </Button>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Rows</Label>
            <Button type="button" variant="outline" size="sm" onClick={addRow}>
              Add row
            </Button>
          </div>
          {rows.map((row, rowI) => (
            <div key={rowI} className="flex flex-wrap gap-2 items-center">
              {row.map((cell, colI) => (
                <Input
                  key={colI}
                  value={cell}
                  onChange={(e) => setCell(rowI, colI, e.target.value)}
                  placeholder=""
                  className="w-24"
                />
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeRow(rowI)}
              >
                Remove row
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
